import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Plus, ChevronLeft, ChevronRight, CheckCircle, XCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import { es } from 'date-fns/locale';

// ── Licencias ──────────────────────────────────────────────
// Autoservicio: cada empleado ve su saldo, pide licencia (queda pendiente
// de aprobación del admin) y consulta el calendario compartido con las
// licencias de todos para no cruzarse. El admin aprueba/rechaza acá mismo.

const TIPOS = ['Vacaciones', 'Médica', 'Estudio', 'Trámite', 'Otro'];

// Color estable por empleado (hash simple del nombre sobre una paleta)
const PALETA = ['#7c6cf8', '#4d8fff', '#f59e0b', '#ef4444', '#a78bfa', '#ec4899', '#22c55e', '#06b6d4'];
const colorDe = (nombre) => {
  let h = 0;
  for (const ch of (nombre || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETA[h % PALETA.length];
};

async function apiLic(method, path, body) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch(`/api${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Error de servidor');
  return data;
}

// Días que descuentan licencia: domingos y feriados regalados NO cuentan
const diasComputables = (from, to, feriados) => {
  const fer = new Set((feriados || []).map(h => h.date));
  let count = 0;
  const d = new Date(from + 'T00:00:00');
  const fin = new Date(to + 'T00:00:00');
  while (d <= fin) {
    const key = format(d, 'yyyy-MM-dd');
    if (d.getDay() !== 0 && !fer.has(key)) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
};

const ST_BADGE = {
  aprobada:  { label: 'Aprobada',  class: 'badge-green' },
  pendiente: { label: 'Pendiente', class: 'badge-amber' },
  rechazada: { label: 'Rechazada', class: 'badge-red'   },
};

export default function LicensesPage() {
  const { currentUser, showToast } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const [me, setMe] = useState(null);          // mi legajo (o null si no hay)
  const [meError, setMeError] = useState('');
  const [cal, setCal] = useState([]);          // calendario compartido
  const [pendientes, setPendientes] = useState([]); // solicitudes (admin)
  const [mes, setMes] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [pedido, setPedido] = useState(null);  // form de solicitud abierto
  const [enviando, setEnviando] = useState(false);
  const [feriados, setFeriados] = useState([]); // días regalados por la empresa
  const [nuevoFeriado, setNuevoFeriado] = useState({ date: '', name: '' });

  const cargar = useCallback(async () => {
    try { setMe(await apiLic('GET', '/licenses/me')); setMeError(''); }
    catch (e) { setMe(null); setMeError(e.message); }
    try { setCal(await apiLic('GET', '/licenses/calendar')); } catch (e) { /* sin calendario */ }
    try { setFeriados(await apiLic('GET', '/holidays')); } catch (e) { /* sin feriados */ }
    if (isAdmin) {
      try { setPendientes(await apiLic('GET', '/licenses/pending')); } catch (e) { /* sin pendientes */ }
    }
  }, [isAdmin]);

  useEffect(() => { cargar(); }, [cargar]);

  // Mi saldo: solo cuentan las aprobadas
  const tomados = (me?.licenses || []).filter(l => l.status === 'aprobada').reduce((s, l) => s + (Number(l.days) || 0), 0);
  const total = me?.licenseTotal || 0;
  const saldo = total - tomados;

  const enviarPedido = async () => {
    if (!pedido?.from || !pedido?.to) { showToast('Completá las fechas', 'error'); return; }
    setEnviando(true);
    try {
      await apiLic('POST', '/licenses/request', pedido);
      showToast('✅ Solicitud enviada — queda pendiente de aprobación');
      setPedido(null);
      cargar();
    } catch (e) { showToast(e.message, 'error'); }
    setEnviando(false);
  };

  const agregarFeriado = async () => {
    if (!nuevoFeriado.date) { showToast('Elegí la fecha', 'error'); return; }
    try {
      await apiLic('POST', '/holidays', nuevoFeriado);
      showToast('🎁 Día regalado marcado');
      setNuevoFeriado({ date: '', name: '' });
      cargar();
    } catch (e) { showToast(e.message, 'error'); }
  };
  const quitarFeriado = async (h) => {
    if (!window.confirm(`¿Quitar el día regalado del ${fmtPatron(h.date, 'dd/MM/yyyy')}?`)) return;
    try { await apiLic('DELETE', `/holidays/${h.id}`); cargar(); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const decidir = async (p, status) => {
    const verbo = status === 'aprobada' ? 'aprobar' : 'rechazar';
    if (!window.confirm(`¿${verbo.charAt(0).toUpperCase() + verbo.slice(1)} la licencia de ${p.empName} (${fmtPatron(p.from, 'dd/MM')} a ${fmtPatron(p.to, 'dd/MM')})?`)) return;
    try {
      await apiLic('PUT', `/licenses/${p.empId}/${p.id}`, { status });
      showToast(status === 'aprobada' ? '✅ Licencia aprobada' : 'Licencia rechazada');
      cargar();
    } catch (e) { showToast(e.message, 'error'); }
  };

  // ── Calendario del mes ──
  const diasDelMes = useMemo(() => {
    const y = mes.getFullYear(), m = mes.getMonth();
    const primero = new Date(y, m, 1);
    const ultimo = new Date(y, m + 1, 0);
    // Lunes = 0
    const offset = (primero.getDay() + 6) % 7;
    const celdas = [];
    for (let i = 0; i < offset; i++) celdas.push(null);
    for (let d = 1; d <= ultimo.getDate(); d++) celdas.push(new Date(y, m, d));
    return celdas;
  }, [mes]);

  const licsDelDia = (dia) => {
    if (!dia) return [];
    const key = format(dia, 'yyyy-MM-dd');
    return cal.filter(l => l.from <= key && key <= l.to);
  };

  const hoyKey = format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Licencias</h1>
          <p className="page-subtitle">Pedí tu licencia y coordiná con el calendario del equipo</p>
        </div>
        {me && (
          <button className="btn btn-primary" onClick={() => setPedido({ from: '', to: '', type: 'Vacaciones', note: '' })}>
            <Plus size={15} /> Pedir licencia
          </button>
        )}
      </div>

      {/* Solicitudes pendientes (solo admin) */}
      {isAdmin && pendientes.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, border: '1px solid rgba(245,158,11,0.4)' }}>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--amber)', marginBottom: 12 }}>
            {pendientes.length} solicitud{pendientes.length !== 1 ? 'es' : ''} pendiente{pendientes.length !== 1 ? 's' : ''}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendientes.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 8, fontSize: 13 }}>
                <strong style={{ minWidth: 140 }}>{p.empName}</strong>
                <span>{fmtPatron(p.from, 'dd/MM/yyyy')} → {fmtPatron(p.to, 'dd/MM/yyyy')}</span>
                <span className="badge badge-blue" style={{ fontSize: 10 }}>{p.type}</span>
                <span style={{ fontWeight: 700 }}>{p.days} día{p.days !== 1 ? 's' : ''}</span>
                {p.note && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>"{p.note}"</span>}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => decidir(p, 'aprobada')}><CheckCircle size={13} /> Aprobar</button>
                  <button className="btn btn-danger btn-sm" onClick={() => decidir(p, 'rechazada')}><XCircle size={13} /> Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 340px) 1fr', gap: 16, alignItems: 'start' }}>
        {/* Mi saldo e historial */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>Mi licencia anual</h3>
            {meError ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{meError}</p>
            ) : !me ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Cargando…</p>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 6px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{total || '—'}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>corresponden</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 6px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{tomados}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>tomados</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 6px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: saldo < 0 ? 'var(--red)' : 'var(--accent)' }}>{total ? saldo : '—'}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>disponibles</div>
                </div>
              </div>
            )}
          </div>

          {me && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 12 }}>Mis licencias</h3>
              {(me.licenses || []).length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin licencias registradas.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[...me.licenses].sort((a, b) => new Date(b.from) - new Date(a.from)).map(l => {
                    const st = ST_BADGE[l.status] || ST_BADGE.aprobada;
                    return (
                      <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '7px 10px', background: 'var(--bg-input)', borderRadius: 8 }}>
                        <span style={{ flex: 1 }}>{fmtPatron(l.from, 'dd/MM/yy')} → {fmtPatron(l.to, 'dd/MM/yy')}</span>
                        <span style={{ color: 'var(--text-muted)' }}>{l.type}</span>
                        <span style={{ fontWeight: 700 }}>{l.days}d</span>
                        <span className={`badge ${st.class}`} style={{ fontSize: 9.5 }}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Calendario compartido */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', margin: 0 }}>Calendario del equipo</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}><ChevronLeft size={14} /></button>
              <strong style={{ fontSize: 13.5, minWidth: 130, textAlign: 'center', textTransform: 'capitalize' }}>{format(mes, 'MMMM yyyy', { locale: es })}</strong>
              <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}><ChevronRight size={14} /></button>
            </div>
          </div>
          <div className="lic-cal-grid">
            {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map(d => (
              <div key={d} style={{ fontSize: 10.5, color: 'var(--text-muted)', textAlign: 'center', padding: '4px 0', fontWeight: 600 }}>{d}</div>
            ))}
            {diasDelMes.map((dia, i) => {
              const lics = licsDelDia(dia);
              const esHoy = dia && format(dia, 'yyyy-MM-dd') === hoyKey;
              const feriado = dia && feriados.find(h => h.date === format(dia, 'yyyy-MM-dd'));
              return (
                <div key={i} className="lic-cal-cell" title={feriado ? `🎁 ${feriado.name}` : undefined} style={{
                  background: feriado ? 'rgba(167,139,250,0.12)' : dia ? 'var(--bg-input)' : 'transparent',
                  border: esHoy ? '1.5px solid var(--accent)' : feriado ? '1px solid rgba(167,139,250,0.45)' : '1px solid transparent',
                }}>
                  {dia && (
                    <>
                      <div style={{ fontSize: 10.5, color: esHoy ? 'var(--accent)' : feriado ? '#a78bfa' : 'var(--text-muted)', fontWeight: (esHoy || feriado) ? 800 : 500, marginBottom: 3 }}>{dia.getDate()}{feriado ? ' 🎁' : ''}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {lics.slice(0, 3).map(l => (
                          <div key={l.licId} title={`${l.empName} · ${l.type} · ${l.status}`} style={{
                            fontSize: 9, fontWeight: 600, padding: '1.5px 4px', borderRadius: 4,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            background: l.status === 'pendiente' ? 'transparent' : colorDe(l.empName) + '33',
                            color: colorDe(l.empName),
                            border: l.status === 'pendiente' ? `1px dashed ${colorDe(l.empName)}` : '1px solid transparent',
                          }}>
                            {l.empName.split(' ')[0]}
                          </div>
                        ))}
                        {lics.length > 3 && <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>+{lics.length - 3}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
            Sólido = aprobada · Punteado = pendiente · 🎁 = día regalado (no descuenta licencia)
          </div>

          {/* Gestión de días regalados (solo admin) */}
          {isAdmin && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <h4 style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 10 }}>🎁 Días regalados (feriados no laborables)</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: 10 }}>Fecha</label>
                  <input type="date" value={nuevoFeriado.date} onChange={e => setNuevoFeriado(f => ({ ...f, date: e.target.value }))} style={{ fontSize: 12 }} />
                </div>
                <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                  <label className="form-label" style={{ fontSize: 10 }}>Nombre</label>
                  <input value={nuevoFeriado.name} onChange={e => setNuevoFeriado(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Día de la Madre" style={{ fontSize: 12 }} />
                </div>
                <button className="btn btn-primary btn-sm" onClick={agregarFeriado}><Plus size={13} /> Marcar</button>
              </div>
              {feriados.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[...feriados].sort((a, b) => a.date.localeCompare(b.date)).map(h => (
                    <span key={h.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '4px 8px', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 16, color: '#a78bfa' }}>
                      {fmtPatron(h.date, 'dd/MM/yy')} · {h.name}
                      <X size={11} style={{ cursor: 'pointer' }} onClick={() => quitarFeriado(h)} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de solicitud */}
      {pedido && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 style={{ fontSize: 16 }}>Pedir licencia</h2>
              <button className="btn btn-secondary btn-sm" style={{ padding: '5px 7px' }} onClick={() => setPedido(null)}><X size={14} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Desde</label>
                  <input type="date" value={pedido.from} onChange={e => setPedido(p => ({ ...p, from: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Hasta</label>
                  <input type="date" value={pedido.to} onChange={e => setPedido(p => ({ ...p, to: e.target.value }))} />
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Tipo</label>
                <select value={pedido.type} onChange={e => setPedido(p => ({ ...p, type: e.target.value }))}>
                  {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nota (opcional)</label>
                <input value={pedido.note} onChange={e => setPedido(p => ({ ...p, note: e.target.value }))} placeholder="Motivo, aclaraciones…" />
              </div>
              {pedido.from && pedido.to && new Date(pedido.to) >= new Date(pedido.from) && (() => {
                const dias = diasComputables(pedido.from, pedido.to, feriados);
                const excede = total > 0 && dias > saldo;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                      Se descuentan <strong>{dias} día{dias !== 1 ? 's' : ''} de licencia</strong> (los domingos y feriados regalados no cuentan).
                      {cal.some(l => l.status !== 'rechazada' && !(l.to < pedido.from || l.from > pedido.to)) && <span style={{ color: 'var(--amber)' }}> ⚠ Hay otra licencia que se superpone con esas fechas — mirá el calendario.</span>}
                    </div>
                    {excede && (
                      <div style={{ fontSize: 12.5, padding: '8px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 8, color: 'var(--amber)' }}>
                        ⚠ Estás pidiendo <strong>{dias} días</strong> pero te quedan <strong>{saldo} disponibles</strong>. Podés enviar la solicitud igual, pero queda a criterio del administrador.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPedido(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={enviando} onClick={enviarPedido}>{enviando ? 'Enviando…' : 'Enviar solicitud'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
